/* eslint-disable jsx-a11y/click-events-have-key-events */
import { CreateUtilInput } from 'features/utils/api/models/CreateUtilInput';
import { UpdateUtilInput } from 'features/utils/api/models/UpdateUtilInput';
import { UtilInfo } from 'features/utils/api/models/UtilInfo';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label } from '@progress/kendo-react-labels';
import { Input } from '@progress/kendo-react-inputs';
import { Editor, EditorTools } from '@progress/kendo-react-editor';
import { EmojiTool } from './EmojiTool';
import { v4 as uuidv4 } from 'uuid';
import styles from './UtilDialog.module.scss';
import { stripHtml } from 'features/utils/utils/htmlText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as fortAwesome from '@fortawesome/free-solid-svg-icons';
import JiraIcon from './jira.svg';
import FigmaIcon from './figma.svg';
import DigitalSignsIcon from './digitalSigns.svg';
import ConfluenceIcon from './confluence.svg';
import UmojoIcon from './umojo.svg';
import Nexparkcon from './nexpark.svg';
import NexcityIcon from './nexcity.svg';
import NextShieldIcon from './nexshield.svg';
import IpmiIcon from './ipmi.png';
import NexaIcon from './nexa.png';
import NpaIcon from './npa.png';
import MediaInferencingIcon from './mediaInferencing.svg';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { categories, DefaultCategory, getCategory, UtilCategory } from 'features/utils/api/models/Category';
import {
  DefaultEnvironment,
  EnvironmentInfo,
  EnvironmentType,
  EnvironmentTypeLookup,
  environmentTypes,
  getEnvironmentType,
} from 'features/utils/api/models/Environment';

interface UtilDialogProps {
  visible: boolean;
  onClose: () => void;
  onSave: (data: CreateUtilInput | UpdateUtilInput) => void;
  initialData: UtilInfo | null;
  isSaving?: boolean;
}

const svgIcons = [
  { iconName: 'umojo', IconComponent: UmojoIcon },
  { iconName: 'nexpark', IconComponent: Nexparkcon },
  { iconName: 'nexcity', IconComponent: NexcityIcon },
  { iconName: 'nexshield', IconComponent: NextShieldIcon },
  { iconName: 'jira', IconComponent: JiraIcon },
  { iconName: 'figma', IconComponent: FigmaIcon },
  { iconName: 'digitalSigns', IconComponent: DigitalSignsIcon },
  { iconName: 'confluence', IconComponent: ConfluenceIcon },
  { iconName: 'mediaInferencing', IconComponent: MediaInferencingIcon },
];

const pngIcons = [
  { iconName: 'ipmiIcon', IconComponent: IpmiIcon },
  { iconName: 'nexa', IconComponent: NexaIcon },
  { iconName: 'npa', IconComponent: NpaIcon },
];

export const awesomeFontIcons = [
  fortAwesome.faWalkieTalkie,
  fortAwesome.faBuildingUser,
  fortAwesome.faPhotoFilm,
  fortAwesome.faMicrophoneLines,
  fortAwesome.faToolbox,
  fortAwesome.faSquareParking,
  fortAwesome.faGear,
  fortAwesome.faTruckFast,
  fortAwesome.faCarSide,
  fortAwesome.faCarTunnel,
  fortAwesome.faChargingStation,
  fortAwesome.faChartLine,
  fortAwesome.faHeadset,
  fortAwesome.faLocationDot,
  fortAwesome.faMapLocationDot,
  fortAwesome.faReceipt,
  fortAwesome.faMoneyBillTransfer,
  fortAwesome.faLandmark,
  fortAwesome.faChartPie,
  fortAwesome.faLightbulb,
  fortAwesome.faDesktop,
  fortAwesome.faPalette,
  fortAwesome.faQuestion,
  fortAwesome.faCircleInfo,
  fortAwesome.faCity,
  fortAwesome.faDatabase,
  fortAwesome.faMicrochip,
  fortAwesome.faPlugCircleBolt,
  fortAwesome.faBridge,
  fortAwesome.faBullhorn,
  fortAwesome.faFilm,
  fortAwesome.faVideo,
  fortAwesome.faTowerBroadcast,
  fortAwesome.faCalendarDay,
  fortAwesome.faTrafficLight,
  fortAwesome.faSignsPost,
  fortAwesome.faCommentDots,
  fortAwesome.faRoad,
  fortAwesome.faGaugeHigh,
  fortAwesome.faSolarPanel,
  fortAwesome.faServer,
];

const isValidUrlFormat = (url: string | null | undefined): boolean => {
  if (!url) {
    return true;
  }
  return url.startsWith('http://') || url.startsWith('https://');
};

const processedSvgIcons = svgIcons.map((svgIcon) => ({
  name: svgIcon.iconName,
  type: 'svg',
  renderData: svgIcon.IconComponent,
}));

const processedPngIcons = pngIcons.map((pngIcon) => ({
  name: pngIcon.iconName,
  type: 'png',
  renderData: pngIcon.IconComponent,
}));

const processedFontAwesomeIcons = awesomeFontIcons.map((faIconDefinition) => ({
  name: faIconDefinition.iconName,
  type: 'fontawesome',
  renderData: faIconDefinition,
}));

export const ALL_ICONS = [...processedSvgIcons, ...processedPngIcons, ...processedFontAwesomeIcons];

const EmptyEnvironment = () => {
  return { id: uuidv4(), environmentType: EnvironmentType.Unknown, order: 0, title: '', url: '' };
};

const DESCRIPTION_MAX_LENGTH = 2000;

const { Bold, Italic, Underline, Strikethrough, OrderedList, UnorderedList, Indent, Outdent, ForeColor, BackColor } =
  EditorTools;

const UtilDialog: React.FC<UtilDialogProps> = ({ visible, onClose, onSave, initialData, isSaving = false }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<UtilCategory>(UtilCategory.Unknown);
  const [description, setDescription] = useState('');
  const [selectedIcon, setSelectedIcon] = useState<string>('');
  const [environments, setEnvironments] = useState<Array<EnvironmentInfo>>([EmptyEnvironment()]);

  useEffect(() => {
    if (visible) {
      setTitle(initialData?.title || '');
      setCategory(initialData?.category || UtilCategory.Unknown);
      setDescription(initialData?.description || '');
      setSelectedIcon(initialData?.icon || svgIcons[0]?.iconName || '');
      setEnvironments(initialData?.environments?.length ? [...initialData.environments] : [EmptyEnvironment()]);
    } else {
      setTitle('');
      setCategory(UtilCategory.Unknown);
      setDescription('');
      setSelectedIcon('');
      setEnvironments([EmptyEnvironment()]);
    }
  }, [visible, initialData]);

  const handleAddEnvironment = () => {
    setEnvironments([...environments, EmptyEnvironment()]);
  };

  const handleRemoveEnvironment = (index: number) => {
    if (environments.length > 1) {
      const newEnvironments = environments.filter((_, i) => i !== index);
      setEnvironments(newEnvironments);
    }
  };

  const handleEnvironmentChange = (index: number, field: keyof EnvironmentInfo, value: string | EnvironmentTypeLookup) => {
    const newEnvironments = [...environments];
    const newValue = field === 'environmentType' ? (value as EnvironmentTypeLookup).value : value;
    newEnvironments[index] = { ...newEnvironments[index], [field]: newValue };
    setEnvironments(newEnvironments);
  };

  const environmentsToSave = environments
    .map((env, index) => ({
      id: env.id || uuidv4(),
      environmentType: env.environmentType || EnvironmentType.Unknown,
      order: index,
      title: env.title || '',
      url: env.url || '',
    })) // Ensure non-null strings
    .filter((env) => env.title.trim() !== '' && env.url.trim() !== ''); // Only save non-empty environments

  const handleSaveClick = useCallback(() => {
    let isValid = true;

    if (!title.trim()) {
      isValid = false;
      alert('Title is required.');
      return;
    }

    if (!category) {
      alert('Category is required.');
      isValid = false;
    }

    if (!selectedIcon) {
      isValid = false;
      alert('Icon selection is required.');
      return;
    }

    environments.forEach((env, index) => {
      const trimmedTitle = (env.title || '').trim();
      const trimmedUrl = (env.url || '').trim();

      if (!trimmedTitle && !trimmedUrl) {
        return;
      }

      if (trimmedUrl) {
        if (!isValidUrlFormat(trimmedUrl)) {
          isValid = false;
          alert(`Environment ${index + 1}: URL format is invalid (must start with http:// or https://).`);
          return;
        }
        if (!trimmedTitle) {
          isValid = false;
          alert(`Environment ${index + 1}: Title is required when URL is provided.`);
          return;
        }
      }
    });

    if (isValid) {
      const saveData: CreateUtilInput | UpdateUtilInput = {
        title,
        category: category!,
        description,
        icon: selectedIcon,
        environments: environmentsToSave,
      };
      onSave(saveData);
    }
  }, [title, category, description, selectedIcon, environmentsToSave, environments, onSave]);

  const handleIconSelect = (iconName: string) => {
    setSelectedIcon(iconName);
  };

  const handleDescriptionChange = (html: string) => {
    const nextHtml = html || '';
    if (stripHtml(nextHtml).length > DESCRIPTION_MAX_LENGTH) {
      return;
    }
    setDescription(nextHtml);
  };

  if (!visible) {
    return null;
  }

  const environmentsAreValid = environments.every(
    (env) => env.title?.trim() !== '' && env.url?.trim() !== '' && isValidUrlFormat(env.url?.trim()),
  );

  const isInvalid = !title || !category || !selectedIcon || !environmentsAreValid;

  const handleCategoryChange = (event: DropDownListChangeEvent) => {
    const { value } = event.value;
    setCategory(value);
  };

  return (
    <Dialog title={initialData ? 'Edit App' : 'Add App'} onClose={onClose} width={605}>
      <div className={styles.dialogContent}>
        <FieldWrapper>
          <Label editorId='serviceTitle'>
            Title <span className={styles.asterisk}>*</span>
          </Label>
          <Input maxLength={128} id='serviceTitle' value={title} onChange={(e) => setTitle(e.target.value?.toString() || '')} />
        </FieldWrapper>

        <FieldWrapper>
          <Label editorId='serviceCategory'>
            Category <span className={styles.asterisk}>*</span>
          </Label>
          <DropDownList
            id='serviceCategory'
            data={categories}
            textField='text'
            dataItemKey='value'
            value={getCategory(category)}
            onChange={handleCategoryChange}
            defaultItem={DefaultCategory}
            className={styles.categoryDropdown}
          />
        </FieldWrapper>

        <FieldWrapper>
          <Label>Description</Label>
          <div className={styles.descriptionEditor}>
            <Editor
              tools={[
                [Bold, Italic, Underline, Strikethrough],
                [ForeColor, BackColor],
                [UnorderedList, OrderedList],
                [Indent, Outdent],
                [EmojiTool],
              ]}
              contentStyle={{ height: 160 }}
              value={description}
              onChange={(e) => handleDescriptionChange(e.html)}
            />
          </div>
        </FieldWrapper>

        <FieldWrapper>
          <Label>Select Icon</Label>
          <div className={styles.iconSelector}>
            {ALL_ICONS.map((iconItem) => {
              let iconContent;
              if (iconItem.type === 'svg' || iconItem.type === 'png') {
                const icon = iconItem.renderData as string;
                iconContent = <img key={iconItem.name} src={icon} alt={iconItem.name} />;
              } else if (iconItem.type === 'fontawesome') {
                iconContent = <FontAwesomeIcon size='2x' icon={iconItem.renderData as IconDefinition} />;
              }
              return (
                <Button
                  key={iconItem.name}
                  className={`${styles.iconButton} ${selectedIcon === iconItem.name ? styles.selected : ''}`}
                  onClick={() => handleIconSelect(iconItem.name)}
                  title={iconItem.name}
                >
                  {iconContent}
                </Button>
              );
            })}
          </div>
        </FieldWrapper>

        <FieldWrapper>
          <Label>
            Environments <span className={styles.asterisk}>*</span>
          </Label>
          {environments.map((env, index) => (
            <div key={index} className={styles.environmentRow}>
              <Input
                maxLength={20}
                placeholder='Title (e.g., Prod)'
                value={env.title || ''}
                onChange={(e) => handleEnvironmentChange(index, 'title', e.target.value?.toString() || '')}
                className={styles.envInput}
              />
              <DropDownList
                id='environmentType'
                data={environmentTypes}
                textField='text'
                dataItemKey='value'
                value={getEnvironmentType(env.environmentType)}
                onChange={(e) => handleEnvironmentChange(index, 'environmentType', e.target.value as EnvironmentTypeLookup)}
                defaultItem={DefaultEnvironment}
                className={styles.envInput}
              />
              <Input
                maxLength={2048}
                placeholder='URL (https://...)'
                value={env.url || ''}
                onChange={(e) => handleEnvironmentChange(index, 'url', e.target.value?.toString() || '')}
                className={styles.envInput}
                type='url'
              />
              <Button
                icon='delete'
                onClick={() => handleRemoveEnvironment(index)}
                title='Remove Environment'
                disabled={environments.length <= 1} // Disable removing if only one row
              />
            </div>
          ))}
          <span onClick={handleAddEnvironment} className={styles.addMoreLink} role='button' tabIndex={0}>
            + Add More
          </span>
        </FieldWrapper>
      </div>

      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button themeColor={'primary'} onClick={handleSaveClick} disabled={isInvalid || isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default UtilDialog;
